import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  OnInit,
  Output,
} from '@angular/core';
import {
  SecurityRiskAcceptance,
  SecurityTarget,
} from '../interfaces/security.interface';
import { SecurityFunctionalChecksService } from '../services/security-functional-checks.service';
import { AppModalData } from '../../shared/ui/app-modal/appModalData';
import { AppModalComponent } from '../../shared/ui/app-modal/app-modal.component';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import { ReloadFunctionalCheckService } from '../services/reload-functional-check.service';
import { SecurityFindingsService } from '../services/security-findings.service';
import { ModalType } from '../../shared/ui/models/modal-type.enum';

@Component({
  selector: 'app-archer-risk-acceptance',
  templateUrl: './archer-risk-acceptance.component.html',
  styleUrls: ['./archer-risk-acceptance.component.scss'],
})
export class ArcherRiskAcceptanceComponent implements OnInit, OnChanges {
  @Input() archerInReview?: SecurityRiskAcceptance;
  @Input() uuid: string;
  @Input() securityFindingId: string;
  @Input() targets: SecurityTarget[];
  @Output() archerSubmitted = new EventEmitter();

  confirmationStep = false;
  submitting = false;
  deleting = false;
  archerId: string;

  constructor(
    private service: SecurityFunctionalChecksService,
    private translate: TranslateService,
    private readonly matDialog: MatDialog,
    private reloadService: ReloadFunctionalCheckService,
    private securityFindingsService: SecurityFindingsService,
  ) {}

  ngOnInit(): void {
    this.deleting = false;
  }

  ngOnChanges(): void {
    this.deleting = false;
  }

  // Backend sets due_date to year 2099 (ArcherValues.NEVER_EXPIRE_DATE) when the
  // Archer finding is held open by a valid permanent acceptance rather than a remediation plan
  get isPermanentAcceptance(): boolean {
    return this.archerInReview?.due_date?.startsWith('2099') ?? false;
  }

  toggleStep() {
    this.confirmationStep = !this.confirmationStep;
  }

  goToConfirmation(id: string) {
    this.archerId = id;
    this.toggleStep();
  }

  uploadRiskAcceptance() {
    this.submitting = true;
    this.service
      .uploadRiskAcceptance(this.uuid, this.securityFindingId, this.archerId)
      .subscribe({
        next: () => {
          this.securityFindingsService.updateSecuritySummary.next(true);
          this.securityFindingsService.updateTableInfo.next({
            showClosedIssues: false,
          });
          this.reloadService.triggerReload();
          this.archerSubmitted.emit(true);
          this.submitting = false;
        },
        error: (err) => {
          // Surface backend-provided error message when available
          const backendMessage = err?.error?.error;
          if (backendMessage) {
            this.service.apiError(err.status, backendMessage);
          }
          this.archerSubmitted.emit(false);
          this.submitting = false;
        },
      });
  }

  openModal() {
    const confirmationModalData: AppModalData = {
      type: ModalType.Warning,
      title: this.translate.instant(
        'component.app-confirmation-modal.remove-archer-functional-check.title',
      ),
      description: this.translate.instant(
        'component.app-confirmation-modal.remove-archer-functional-check.description',
      ),
      detailDescription: this.translate.instant('component.common.disclaimer'),
      buttonYesText: this.translate.instant('common.remove'),
      buttonNoText: this.translate.instant('common.cancel'),
    };
    const dialogRef = this.matDialog.open(AppModalComponent, {
      panelClass: 'dialogPanelClass',
      data: confirmationModalData,
    });
    dialogRef.afterClosed().subscribe((result) => {
      if (result) {
        this.deleting = true;
        this.service
          .deleteRiskAcceptance(this.uuid, this.securityFindingId)
          .subscribe({
            next: () => {
              this.securityFindingsService.updateSecuritySummary.next(true);
              this.securityFindingsService.updateTableInfo.next({
                showClosedIssues: false,
              });
              this.reloadService.triggerReload();
            },
            error: (err) => {
              this.service.apiError(err.status, err.error.return_message);
              this.deleting = false;
            },
          });
      }
    });
  }
}
